import * as React from "react"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          key: `${item}-${index}`,
          label: item,
          subtitle: "",
        }
      }

      const label = String(item?.name || item?.label || "").trim()
      if (!label) {
        return null
      }

      const subtitle = Number(item?.match_count) > 0
        ? `${Number(item.match_count)} match${Number(item.match_count) === 1 ? "" : "es"}`
        : ""

      return {
        key: `${item?.id || label}-${index}`,
        label,
        subtitle,
      }
    })
    .filter(Boolean)
}

function TagListPreview({
  items,
  maxVisible = 2,
  emptyLabel = "-",
  title = "Tags",
  tone = "teal",
}) {
  const normalizedItems = React.useMemo(() => normalizeItems(items), [items])

  if (normalizedItems.length === 0) {
    return <span className="text-xs text-muted-foreground">{emptyLabel}</span>
  }

  const visibleItems = normalizedItems.slice(0, maxVisible)
  const toneClasses = tone === "sky"
    ? {
        chip: "bg-sky-500/15 text-sky-300",
        trigger: "border-sky-500/20 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20",
      }
    : {
        chip: "bg-teal-500/15 text-teal-300",
        trigger: "border-teal-500/20 bg-teal-500/10 text-teal-300 hover:bg-teal-500/20",
      }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibleItems.map((item) => (
        <span
          key={item.key}
          className={cn("inline-flex max-w-[120px] truncate rounded-md px-2 py-0.5 text-[10px] font-medium", toneClasses.chip)}
          title={item.label}
        >
          {item.label}
        </span>
      ))}
      {normalizedItems.length > maxVisible && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(event) => event.stopPropagation()}
              className={cn("inline-flex rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors", toneClasses.trigger)}
            >
              +{normalizedItems.length - maxVisible}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 border-border bg-card p-3" onClick={(event) => event.stopPropagation()}>
            <div className="space-y-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{normalizedItems.length} total</p>
              </div>
              <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                {normalizedItems.map((item) => (
                  <div key={item.key} className="rounded-lg border border-border bg-background/40 px-3 py-2">
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    {item.subtitle && (
                      <p className="mt-1 text-[11px] text-muted-foreground">{item.subtitle}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

export { TagListPreview }
